import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Controller, FieldArrayWithId, useFormContext } from 'react-hook-form';
import { ThemeProvider } from 'styled-components';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { FormValues, Purchase as PurchaseDTO, WhatIfDTO } from './AnnuityPurchase.meta';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { Separator } from '../../../../../../../CommonComponents/OPL/Separator/Separator';
import { IconButton } from '../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Button } from '../../../../../../../CommonComponents/OPL/Button/Button';
import { AnnuityPurchaseOptions } from '../../../../api/useWhatIfOptionsQuery';
import { PurchaseAmountForm } from './components/PurchaseAmountForm/PurchaseAmountForm';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';
import {
  AddAccountSelectionModalWrapper,
  Selection,
} from '../../../../../../../Profile/Accounts/components/AddAccountSelectionModalWrapper/AddAccountSelectionModalWrapper';
import { theme } from '../../../../../../../theme/theme';
import {
  ANNUITY_TYPE_OPTIONS,
  LocalSaveOptionsData,
} from '../../../../../../../Profile/Accounts/Features/AccountPreferences/components/AccountForm/AccountForm.meta';
import { usePurchasesFieldArray } from './usePurchasesFieldArray';
import { HelperText } from '../../../../../../../CommonComponents/OPL/HelperText/HelperText';
import { AccountModalUsageContext } from '../../../../../../../Profile/Accounts/Features/AccountPreferences/components/AccountForm/context/AccountFormProvider.meta';
import { selectCurrentClient } from '../../../../../../../CommonSelectors/CommonAreaSelector';

const NEW_PURCHASE: Partial<PurchaseDTO> = {
  IsApplied: false,
  SourceAccounts: [],
  PurchaseYear: null,
  PurchaseAmount: null,
  UseFullAccountValue: false,
  Annuity: null,
};

interface Props {
  Title: string;
  WhatIf: WhatIfDTO;
  options: AnnuityPurchaseOptions;
  fpId: string;
}

export const AnnuityPurchase = ({ Title, options, fpId }: Props) => {
  const purchasesFieldArray = usePurchasesFieldArray();

  return (
    <>
      <Text fontSize="h5" fontWeight={600} px="12" py="16">
        What-if...
      </Text>

      <Separator />

      <Box display="flex" flexDirection="column" p={12} style={{ gap: 16 }}>
        <Text fontSize="h6" fontWeight={500}>
          {Title}
        </Text>

        <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
          {purchasesFieldArray.fields.map((purchase, index) => (
            <Purchase
              key={purchase.key}
              purchase={purchase}
              index={index}
              fpId={fpId}
              onDelete={() => purchasesFieldArray.remove(index)}
              options={options}
            />
          ))}
        </Box>

        {purchasesFieldArray.fields.every(purchase => purchase.IsApplied) && (
          <Button
            variant="link"
            leftIcon="fas fa-plus"
            onClick={() => purchasesFieldArray.append(NEW_PURCHASE as PurchaseDTO)}
            style={{ alignSelf: 'flex-start' }}
          >
            {`Add ${purchasesFieldArray.fields.length > 0 ? 'Additional ' : ''}Purchase`}
          </Button>
        )}
      </Box>
    </>
  );
};

interface PurchaseProps {
  purchase: FieldArrayWithId<FormValues, 'Purchases', 'key'>;
  index: number;
  fpId: string;
  options: AnnuityPurchaseOptions;
  onDelete(): void;
}

const Purchase = ({ purchase, index, fpId, onDelete, options }: PurchaseProps) => {
  const formBag = useFormContext<FormValues>();

  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);

  const { Dob: primaryDob, PartnerDob: spouseDob } = useSelector(selectCurrentClient);

  const primaryYob = primaryDob ? new Date(primaryDob).getFullYear() : null;
  const spouseYob = spouseDob ? new Date(spouseDob).getFullYear() : null;

  const purchaseYearOptions = options.YearsOptions.map(year => ({
    label: year.toString(),
    value: year,
    rightLabel: primaryYob
      ? `Age ${year - primaryYob}${spouseYob ? `, Age ${year - spouseYob}` : ''}`
      : undefined,
  }));

  const onAnnuityAccountSave = (data: LocalSaveOptionsData) => {
    formBag.setValue(`Purchases.${index}.Annuity`, data.bankAccount, {
      shouldValidate: true,
      shouldDirty: true,
    });

    if (
      data.bankAccount?.OwnerModel.length !== purchase.Annuity?.OwnerModel.length ||
      !(
        data.bankAccount?.OwnerModel.length === purchase.Annuity?.OwnerModel.length &&
        data.bankAccount?.OwnerModel?.every(
          (owner, index) => owner.Name === purchase.Annuity?.OwnerModel[index].Name,
        )
      ) ||
      data.bankAccount?.AnnuityIsQualified !== purchase.Annuity?.AnnuityIsQualified
    ) {
      formBag.setValue(`Purchases.${index}.SourceAccounts`, []);
      formBag.setValue(`Purchases.${index}.PurchaseAmount`, null);
    }
  };

  const isReadOnly = purchase.IsApplied || purchase.Annuity === null;

  return (
    <>
      <React.Fragment key={purchase.key}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Text fontWeight={500}>{`Annuity Purchase ${index + 1}`}</Text>

          {!purchase.IsApplied && (
            <IconButton variant="subtleInactive" icon="far fa-minus-circle" onClick={onDelete} />
          )}
        </Box>

        {purchase.Annuity !== null ? (
          <Box
            border="1px solid"
            borderColor="neutral.200"
            borderRadius="medium"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            style={{ gap: 4 }}
            p="16"
          >
            <Box as="dl" display="flex" flexDirection="column" style={{ gap: 8 }}>
              <Box>
                <Text as="dt" variant="smallCaps" color="neutral.600">
                  Annuity tax Status
                </Text>

                <Text as="dd" fontSize="small">
                  {purchase.Annuity.AnnuityIsQualified ? 'Qualified' : 'Non-Qualified'}
                </Text>
              </Box>

              <Box>
                <Text as="dt" variant="smallCaps" color="neutral.600">
                  Annuity Type
                </Text>

                <Text as="dd" fontSize="small">
                  {ANNUITY_TYPE_OPTIONS.find(({ value }) => value === purchase.Annuity?.AnnuityType)
                    ?.label || 'Unknown annuity type'}
                </Text>
              </Box>

              {purchase.Annuity.AccountName && (
                <Box>
                  <Text as="dt" variant="smallCaps" color="neutral.600">
                    Annuity Account Description
                  </Text>

                  <Text as="dd" fontSize="small">
                    {purchase.Annuity.AccountName}
                  </Text>
                </Box>
              )}
            </Box>

            {!isReadOnly && (
              <IconButton
                icon="far fa-pencil"
                variant="subtleInactive"
                aria-label="Edit field"
                onClick={() => setIsAccountModalOpen(true)}
              />
            )}
          </Box>
        ) : (
          <Box display="flex" flexDirection="column">
            <Button
              variant="secondary"
              leftIcon="fas fa-plus"
              onClick={() => setIsAccountModalOpen(true)}
            >
              Add Annuity Account Details
            </Button>

            {formBag.formState.errors.Purchases?.[index]?.Annuity && (
              <HelperText variant="error" mt={4}>
                {(formBag.formState.errors.Purchases?.[index]?.Annuity as any)?.message}
              </HelperText>
            )}
          </Box>
        )}

        <Controller
          control={formBag.control}
          name={`Purchases.${index}.PurchaseYear`}
          render={({ field: { ref, value, onChange } }) => {
            return (
              <Select
                innerRef={ref}
                label="When would you like to purchase this annuity?"
                value={purchaseYearOptions.find(({ value: v }) => v === value)}
                options={purchaseYearOptions}
                onChange={opt => onChange(opt?.value)}
                formVariant={
                  formBag.formState.errors.Purchases?.[index]?.PurchaseYear ? 'error' : undefined
                }
                helperText={formBag.formState.errors.Purchases?.[index]?.PurchaseYear?.message}
                disabled={isReadOnly}
              />
            );
          }}
        />

        <PurchaseAmountForm
          purchase={purchase}
          index={index}
          fpId={fpId}
          isReadOnly={isReadOnly}
          accountOwner={purchase.Annuity?.AccountOwner || null}
          ownerModel={purchase.Annuity?.OwnerModel || null}
          isAnnuityQualified={purchase.Annuity?.AnnuityIsQualified || false}
        />
      </React.Fragment>

      {isAccountModalOpen && (
        <ThemeProvider theme={theme}>
          <AddAccountSelectionModalWrapper
            initialSelection={Selection.Manual}
            usageContext={AccountModalUsageContext.AnnuityPurchaseWhatIf}
            showExternalProviders={false}
            onClose={() => setIsAccountModalOpen(false)}
            localSaveOptions={{
              data: {
                bankAccount: purchase.Annuity,
                accountPortfolio: null,
              },
              onSave: onAnnuityAccountSave,
              showHypotheticalReturn: false,
            }}
          />
        </ThemeProvider>
      )}
    </>
  );
};
